import { useState } from 'react';
import { MapPin, Phone, Mail, Globe, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHero from '../../components/public/PageHero';
import Reveal from '../../components/public/Reveal';
import api from '../../lib/api';
import { CONTACT_INFO } from '../../lib/constants';

const INFO_CARDS = [
  { icon: MapPin, label: 'Oficina central', value: CONTACT_INFO.address },
  { icon: Phone, label: 'Teléfono', value: CONTACT_INFO.phone },
  { icon: Mail, label: 'Correo', value: CONTACT_INFO.email },
  { icon: Globe, label: 'Sitio web', value: CONTACT_INFO.web },
];

const EMPTY = { name: '', email: '', phone: '', subject: '', message: '' };

export default function Contacto() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/contact', form);
      setSent(true);
      setForm(EMPTY);
      toast.success('Mensaje enviado. Te contactaremos pronto.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar el mensaje. Inténtalo de nuevo.');
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';

  return (
    <div>
      <PageHero
        eyebrow="Contacto"
        title="Hablemos de tu futuro en el sector"
        description="Escríbenos y un asesor de la CNIB te responderá a la brevedad."
      />

      <section className="py-20 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <div className="space-y-5 lg:col-span-2">
            {INFO_CARDS.map((card, i) => (
              <Reveal key={card.label} delay={i * 0.08}>
                <div className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cnib-blue/10">
                    <card.icon className="h-5 w-5 text-cnib-blue" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{card.label}</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800">{card.value}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm md:p-10">
              {sent ? (
                <div className="flex flex-col items-center py-14 text-center" data-testid="contact-success">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                  </span>
                  <h3 className="mt-6 font-heading text-2xl font-bold text-slate-900">Mensaje recibido</h3>
                  <p className="mt-2 max-w-sm text-slate-600">
                    Gracias por escribirnos. Recibirás una confirmación en tu correo y te responderemos pronto.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-6 rounded-full border-2 border-cnib-blue px-6 py-2.5 text-sm font-bold text-cnib-blue transition-colors hover:bg-cnib-blue hover:text-white"
                    data-testid="contact-send-another"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">Nombre completo *</label>
                      <input name="name" value={form.name} onChange={handleChange} required minLength={2} className={inputCls} placeholder="Tu nombre" data-testid="contact-name-input" />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">Correo electrónico *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputCls} placeholder="tucorreo@ejemplo.com" data-testid="contact-email-input" />
                    </div>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">Teléfono / WhatsApp</label>
                      <input name="phone" value={form.phone} onChange={handleChange} className={inputCls} placeholder="+591 ..." data-testid="contact-phone-input" />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">Asunto *</label>
                      <input name="subject" value={form.subject} onChange={handleChange} required minLength={2} className={inputCls} placeholder="¿En qué podemos ayudarte?" data-testid="contact-subject-input" />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700">Mensaje *</label>
                    <textarea name="message" value={form.message} onChange={handleChange} required minLength={5} rows={5} className={inputCls} placeholder="Cuéntanos más..." data-testid="contact-message-input" />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-form-submit-button"
                    className="group flex w-full items-center justify-center gap-2 rounded-full bg-orange-500 py-3.5 font-bold text-white shadow-lg shadow-orange-500/25 transition-all duration-300 hover:bg-orange-600 disabled:opacity-60 sm:w-auto sm:px-10"
                  >
                    {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                    {sending ? 'Enviando…' : 'Enviar mensaje'}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
